// F-092 Portail propriétaire (compte du mandant, hors espace) et F-093 page publique de validation d'une dépense.
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { PublicShell } from "@core/layout";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useSession } from "@core/session";
import { Button, Card, Empty, ErrorBox, Input, KeyValues, Loading, Notice, Stat, Table, Textarea, fieldError } from "@core/ui";
import { LEASE_STATUS, OWNER_KINDS, REGIMES, Status, useRun } from "./shared";

/** En-tête aux couleurs du gestionnaire, sans masquer la fourniture par Bailivo. */
function BrandHeader({ branding, fallback }: { branding: any; fallback: string }) {
  if (!branding) return <p className="eyebrow">{fallback}</p>;
  return (
    <div className="stack-sm">
      <div className="pro-brand" style={{ background: branding.accent_hex, color: branding.on_accent_hex }}>
        {branding.logo_url ? <img src={branding.logo_url} alt={`Logo de ${branding.display_name}`} /> : null}
        <strong>{branding.display_name}</strong>
      </div>
      <p className="small muted">{branding.provided_by}</p>
    </div>
  );
}

function Support({ branding }: { branding: any }) {
  if (!branding) return null;
  const s = branding.support ?? {};
  if (!s.email && !s.phone && !s.hours && !s.url && !branding.legal_notice) return null;
  return (
    <Card title="Contacter votre gestionnaire">
      <KeyValues
        items={[
          ["E-mail", s.email ? <a href={`mailto:${s.email}`}>{s.email}</a> : "—"],
          ["Téléphone", s.phone ?? "—"],
          ["Horaires", s.hours || "—"],
          ["Aide en ligne", s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.url}</a> : "—"],
        ]}
      />
      {branding.legal_notice ? <p className="small muted">{branding.legal_notice}</p> : null}
    </Card>
  );
}

export function PortalListPage() {
  const q = useGet<any>("/api/v1/pro/portal");
  return (
    <PublicShell wide>
      <h1>Portail propriétaire</h1>
      <p className="intro">Les biens que des gestionnaires administrent pour vous, en lecture : logements, baux, loyers encaissés, dépenses, documents partagés et rapports.</p>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !q.data.items.length ? (
        <Card>
          <Empty title="Aucun accès propriétaire">Votre gestionnaire vous envoie une invitation par e-mail. Ouvrez le lien reçu en étant connecté avec cette adresse.</Empty>
        </Card>
      ) : null}
      <div className="pro-links">
        {(q.data?.items ?? []).map((p: any) => (
          <Link key={p.owner_id} to={`/moi/proprietaire/${p.owner_id}`} className="pro-link">
            <strong>{p.owner_name}</strong>
            <span className="small block">
              {OWNER_KINDS[p.owner_kind] ?? p.owner_kind} · géré par {p.org_name}
            </span>
            {p.pending_approvals > 0 ? <span className="small block">{p.pending_approvals} demande(s) d’accord en attente</span> : null}
          </Link>
        ))}
      </div>
      <p className="small">
        <Link to="/moi">Revenir à mon espace personnel</Link>
      </p>
    </PublicShell>
  );
}

export function PortalInvitationPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const q = useGet<any>(`/api/v1/pro/owner-invitations/${token}`);
  const { busy, error, run } = useRun();
  return (
    <PublicShell>
      <h1>Invitation au portail propriétaire</h1>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="stack">
          <p>
            « {q.data.org_name} » vous invite à consulter les informations de « {q.data.owner_name} ».
          </p>
          {user && user.email.toLowerCase() !== String(q.data.email).toLowerCase() ? (
            <Notice tone="attention">Cette invitation est destinée à {q.data.email}. Déconnectez-vous puis connectez-vous avec cette adresse.</Notice>
          ) : null}
          <ErrorBox error={error} />
          <Button
            busy={busy}
            onClick={async () => {
              const r: any = await run(() => post(`/api/v1/pro/owner-invitations/${token}/accept`));
              if (r) navigate(`/moi/proprietaire/${r.owner_id}`);
            }}
          >
            Accéder au portail
          </Button>
        </div>
      ) : null}
    </PublicShell>
  );
}

export function PortalPage() {
  const { ownerId = "" } = useParams();
  const q = useGet<any>(`/api/v1/pro/portal/${ownerId}`);
  const payments = useGet<any>(`/api/v1/pro/portal/${ownerId}/payments`);
  if (q.isLoading) {
    return (
      <PublicShell wide>
        <Loading />
      </PublicShell>
    );
  }
  if (!q.data) {
    return (
      <PublicShell>
        <h1>Portail introuvable</h1>
        <p>Cet accès n’existe pas ou a été retiré par votre gestionnaire.</p>
        <Link to="/moi/proprietaire">Voir mes accès</Link>
      </PublicShell>
    );
  }
  const d = q.data;
  const pending = (d.expenses as any[]).filter((e) => e.status === "en_attente");
  return (
    <PublicShell wide>
      <div className="stack">
        <BrandHeader branding={d.branding} fallback={`Géré par ${d.org.name}`} />
        <h1>{d.owner.display_name}</h1>
        <p className="intro">Consultation en lecture des informations tenues par {d.org.name}. Les dossiers des candidats et les notes internes de l’équipe ne sont pas partagés.</p>
        <div className="stats">
          <Stat label="Logements" value={d.properties.length} />
          <Stat label="Encaissements sur 12 mois" value={money(d.rents.paid_cents)} />
          <Stat label="Loyers appelés sur 12 mois" value={money(d.rents.called_cents)} hint="Selon les appels enregistrés" />
          <Stat label="Accords attendus" value={pending.length} />
        </div>
        {pending.length && d.can_approve ? <Approvals ownerId={ownerId} items={pending} /> : null}
        {pending.length && !d.can_approve ? <Notice>Des demandes d’accord sont en attente ; votre accès est en lecture seule.</Notice> : null}
        <Card title="Logements et baux">
          {!d.properties.length ? <p className="muted">Aucun logement.</p> : null}
          {d.leases.length ? (
            <Table caption="Baux">
              <thead>
                <tr>
                  <th>Logement</th>
                  <th>Régime</th>
                  <th>Statut</th>
                  <th>Début</th>
                  <th>Locataire</th>
                  <th className="num">Loyer</th>
                  <th className="num">Charges</th>
                </tr>
              </thead>
              <tbody>
                {d.leases.map((l: any) => (
                  <tr key={l.id}>
                    <td>{l.property_label}</td>
                    <td>{REGIMES[l.regime] ?? l.regime}</td>
                    <td>{LEASE_STATUS[l.status] ?? l.status}</td>
                    <td>{date(l.start_on)}</td>
                    <td>{l.tenants ?? "—"}</td>
                    <td className="num">{money(l.rent_cents)}</td>
                    <td className="num">{money(l.charges_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <ul className="list-plain">
              {d.properties.map((p: any) => (
                <li key={p.id}>{p.label}</li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Loyers encaissés">
          {payments.data && !payments.data.items.length ? <p className="muted">Aucun encaissement enregistré sur la période.</p> : null}
          {payments.data?.items.length ? (
            <Table caption="Encaissements">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Logement</th>
                  <th className="num">Montant</th>
                </tr>
              </thead>
              <tbody>
                {payments.data.items.map((p: any) => (
                  <tr key={p.id}>
                    <td>{date(p.received_on)}</td>
                    <td>{p.property_label}</td>
                    <td className="num">{money(p.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>
        <Card title="Dépenses">
          {!d.expenses.length ? <p className="muted">Aucune dépense suivie.</p> : null}
          {d.expenses.length ? (
            <Table caption="Dépenses">
              <thead>
                <tr>
                  <th>Objet</th>
                  <th>Logement</th>
                  <th className="num">Montant</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {d.expenses.map((e: any) => (
                  <tr key={e.id}>
                    <td>
                      {e.title}
                      {e.supplier ? <span className="block muted small">{e.supplier}</span> : null}
                    </td>
                    <td>{e.property_label ?? "—"}</td>
                    <td className="num">{money(e.approved_amount_cents ?? e.amount_cents)}</td>
                    <td>
                      <Status kind="expense" value={e.status} />
                      {e.urgent ? <span className="block small">Engagée en urgence</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>
        <Card title="Documents et rapports">
          {!d.documents.length ? <p className="muted">Aucun document partagé.</p> : null}
          <ul className="list-plain">
            {d.documents.map((doc: any) => (
              <li key={doc.id}>
                <a href={`/api/v1/pro/portal/${ownerId}/documents/${doc.id}/fichier`} target="_blank" rel="noreferrer">
                  {doc.title || doc.filename}
                </a>
                <span className="muted small"> · partagé le {dateTime(doc.shared_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Support branding={d.branding} />
        <p className="small">
          <Link to="/moi/proprietaire">Tous mes accès propriétaire</Link>
        </p>
      </div>
    </PublicShell>
  );
}

function Approvals({ ownerId, items }: { ownerId: string; items: any[] }) {
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [comment, setComment] = useState<Record<string, string>>({});
  const decide = async (id: string, decision: string) => {
    if (await run(() => post(`/api/v1/pro/portal/${ownerId}/approvals/${id}/decision`, { decision, comment: comment[id] ?? "" }), decision === "approuver" ? "Accord enregistré." : "Refus enregistré.")) {
      invalidate(`/api/v1/pro/portal`);
    }
  };
  return (
    <Card title="Demandes d’accord">
      <p className="small">Votre accord autorise votre gestionnaire à engager la dépense. Il ne vaut ni signature du devis ni paiement.</p>
      <ErrorBox error={error} />
      <ul className="list-plain stack">
        {items.map((e) => (
          <li key={e.id} className="stack-sm">
            <strong>
              {e.title} — {money(e.amount_cents)}
            </strong>
            <span className="small">
              {e.property_label ?? "Logement non précisé"}
              {e.supplier ? ` · ${e.supplier}` : ""} · {e.approval_reason}
            </span>
            {e.token_expires_at ? <span className="small muted">Réponse attendue avant le {dateTime(e.token_expires_at)}</span> : null}
            <Textarea label="Commentaire (obligatoire en cas de refus)" value={comment[e.id] ?? ""} error={fieldError(error, "comment")} onChange={(ev) => setComment({ ...comment, [e.id]: ev.target.value })} />
            <div className="row">
              <Button busy={busy} onClick={() => decide(e.id, "approuver")}>
                Approuver la dépense
              </Button>
              <Button variant="secondaire" busy={busy} onClick={() => decide(e.id, "refuser")}>
                Refuser
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Page publique ouverte depuis le lien reçu par e-mail : pas de compte nécessaire. */
export function ExpenseApprovalPage() {
  const { token = "" } = useParams();
  const q = useGet<any>(`/api/v1/pro/expense-approvals/${token}`);
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ name: "", comment: "" });
  const [done, setDone] = useState<string | null>(null);
  const d = q.data;
  return (
    <PublicShell>
      {d ? <BrandHeader branding={d.branding} fallback={d.org_name} /> : null}
      <h1>Demande d’accord sur une dépense</h1>
      {q.isLoading ? <Loading /> : null}
      {!d && !q.isLoading ? <Notice tone="attention">{q.error instanceof Error ? q.error.message : "Ce lien n’est plus valable."}</Notice> : null}
      {done ? (
        <Notice tone="succes" title={done === "approuvee" ? "Accord enregistré" : "Refus enregistré"}>
          Votre réponse a été transmise à votre gestionnaire et consignée dans le journal de la demande. Vous pouvez fermer cette page.
        </Notice>
      ) : null}
      {d && !done ? (
        <div className="stack">
          <p>
            « {d.org_name} » vous demande votre accord avant d’engager cette dépense{d.owner_name ? ` pour « ${d.owner_name} »` : ""}.
          </p>
          <KeyValues
            items={[
              ["Objet", d.title],
              ["Logement", d.property_label ?? "—"],
              ["Fournisseur", d.supplier || "—"],
              ["Montant", money(d.amount_cents)],
              ["Motif de la demande", d.approval_reason],
              ["Réponse attendue avant le", dateTime(d.expires_at)],
            ]}
          />
          {d.description ? <p>{d.description}</p> : null}
          {d.has_quote ? (
            <p>
              <a href={`/api/v1/pro/expense-approvals/${token}/devis`} target="_blank" rel="noreferrer">
                Consulter le devis
              </a>
            </p>
          ) : (
            <Notice tone="attention">Aucun devis n’est joint à cette demande.</Notice>
          )}
          <Notice>Votre accord autorise l’engagement de la dépense. Il ne vaut ni signature du devis ni paiement.</Notice>
          <Input label="Votre nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Textarea label="Commentaire (obligatoire en cas de refus)" value={form.comment} error={fieldError(error, "comment")} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          <ErrorBox error={error && !fieldError(error, "comment") ? error : null} />
          <div className="row">
            <Button
              busy={busy}
              onClick={async () => {
                const r: any = await run(() => post(`/api/v1/pro/expense-approvals/${token}/decision`, { decision: "approuver", ...form, expected_amount_cents: d.amount_cents }));
                if (r) setDone(r.status);
              }}
            >
              Approuver la dépense
            </Button>
            <Button
              variant="secondaire"
              busy={busy}
              onClick={async () => {
                const r: any = await run(() => post(`/api/v1/pro/expense-approvals/${token}/decision`, { decision: "refuser", ...form, expected_amount_cents: d.amount_cents }));
                if (r) setDone(r.status);
              }}
            >
              Refuser
            </Button>
          </div>
        </div>
      ) : null}
    </PublicShell>
  );
}
